import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { client } from "@/lib/api";
import { Cake, ExternalLink } from "lucide-react";

/**
 * ProfessorOfTheDayStrip — Iter223.
 *
 * Faixa discreta que só aparece na Home quando algum docente cadastrado
 * faz aniversário HOJE (backend endpoint /api/hero/professor-of-the-day
 * retorna `{item: null}` nos outros dias — o componente não renderiza,
 * sem CLS extra).
 *
 * Design intencionalmente contido:
 *   - 1 linha em desktop, 2 linhas em mobile
 *   - fundo âmbar suave (~5% opacity) para não competir com o Hero
 *   - CTA linkado à página pública do(a) autor(a) quando disponível
 */
export default function ProfessorOfTheDayStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await client.get("/hero/professor-of-the-day");
        if (!cancelled) setData(res.data);
      } catch {
        // Silent fail — feature é opcional; não bloqueia a home.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Não renderiza se: ainda carregando, sem item, ou erro silencioso.
  if (loading || !data || !data.item) return null;
  const p = data.item;
  const target = p.author_url || "/autores";

  return (
    <div className="max-w-6xl mx-auto px-4 pt-4" data-testid="professor-of-the-day-strip">
      <Link
        to={target}
        className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/[0.07] via-amber-500/[0.04] to-transparent px-4 py-2.5 hover:from-amber-500/[0.1] hover:to-amber-500/[0.02] transition group"
      >
        <div className="shrink-0 w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
          <Cake className="w-4 h-4 text-amber-200" />
        </div>
        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-amber-300/90">
            Professor(a) do Dia
          </span>
          <span className="text-sm font-semibold text-white truncate">
            {p.name}
          </span>
          {p.institution && (
            <span className="text-[11px] text-slate-500 truncate">
              · {p.institution}
            </span>
          )}
          {data.total_today > 1 && (
            <span className="text-[10px] text-amber-400/70">
              (+{data.total_today - 1} aniversariante{data.total_today - 1 > 1 ? "s" : ""})
            </span>
          )}
        </div>
        <span className="shrink-0 text-[11px] text-amber-300/80 inline-flex items-center gap-1 group-hover:text-amber-300">
          Ver perfil <ExternalLink className="w-3 h-3" />
        </span>
      </Link>
    </div>
  );
}
